import { Parser } from "htmlparser2";

/**
 * 标签闭合回调的结果类型（包含完整HTML片段）
 */
export interface CompleteTagHtmlResult {
  /** 标签名（如div、p、img） */
  tagName: string;
  /** 是否为自闭合标签（如img、input） */
  isSelfClosing: boolean;
  /** 该标签对应的完整、可独立渲染的HTML片段 */
  completeHtml: string;
  /** 标签属性（键值对） */
  attributes: Record<string, string>;
  /** 标签层级（嵌套深度，根标签为0） */
  level: number;
}

/**
 * 手动输入HTML的解析器配置项
 */
export interface ManualHtmlParserOptions {
  /** 每个标签闭合时的回调，返回完整HTML片段 */
  onTagComplete: (result: CompleteTagHtmlResult) => void;
  /** 解析完成的回调（所有HTML输入完毕并解析完成时触发） */
  onParseEnd?: () => void;
  /** 解析错误的回调 */
  onParseError?: (error: Error) => void;
}

/**
 * 手动添加HTML字符串的解析器类
 * 支持单次/分块传入HTML，标签闭合时返回完整片段
 */
export class HtmlParser {
  private parser: ReturnType<typeof this.createParser>; // 解析器实例
  private fullHtmlBuffer = ""; // 存储完整的HTML内容
  private tagStack: Array<{
    name: string;
    start: number;
    attrs: Record<string, string>;
    level: number;
    isSelfClosing: boolean;
  }> = []; // 标签栈
  private currentLevel = -1; // 当前嵌套层级
  private isFinished = false; // 是否已结束解析

  constructor(private options: ManualHtmlParserOptions) {
    this.parser = this.createParser();
  }

  /**
   * 创建htmlparser2解析器实例
   */
  private createParser() {
    return new Parser(
      {
        /** 标签打开时的处理逻辑 */
        onopentag: (
          tagName: string,
          attrs: Record<string, string>,
          selfClosing: boolean
        ) => {
          try {
            this.currentLevel++;
            const startPos = this.fullHtmlBuffer.length;

            // 拼接标准的开标签（带属性，自闭合标签补充/>）
            const attrsStr = Object.entries(attrs)
              .map(
                ([k, v]) =>
                  `${k}="${v.replace(/"/g, "&quot;").replace(/\n/g, "&#10;")}"`
              )
              .join(" ");
            const openTag = selfClosing
              ? attrsStr
                ? `<${tagName} ${attrsStr}/>`
                : `<${tagName}/>`
              : attrsStr
              ? `<${tagName} ${attrsStr}>`
              : `<${tagName}>`;

            this.fullHtmlBuffer += openTag;

            // 推入标签栈
            this.tagStack.push({
              name: tagName,
              start: startPos,
              attrs: { ...attrs },
              level: this.currentLevel,
              isSelfClosing: selfClosing,
            });

            // 自闭合标签直接触发完成回调
            if (selfClosing) {
              this.triggerTagComplete(tagName, true);
              this.currentLevel--;
            }
          } catch (err) {
            this.options.onParseError?.(err as Error);
          }
        },

        /** 标签闭合时的处理逻辑 */
        onclosetag: (tagName: string) => {
          try {
            // 拼接闭标签
            this.fullHtmlBuffer += `</${tagName}>`;
            // 触发完成回调
            this.triggerTagComplete(tagName, false);
            this.currentLevel--;
          } catch (err) {
            this.options.onParseError?.(err as Error);
          }
        },

        /** 文本节点处理 */
        ontext: (text: string) => {
          this.fullHtmlBuffer += text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
        },

        /** 注释节点处理 */
        oncomment: (comment: string) => {
          this.fullHtmlBuffer += `<!--${comment}-->`;
        },

        /** 解析错误处理 */
        onerror: (error: Error) => {
          this.options.onParseError?.(error);
        },
      },
      {
        decodeEntities: true,
        recognizeSelfClosing: true,
        xmlMode: false,
        lowerCaseTags: false,
      }
    );
  }

  /**
   * 触发标签完成回调，生成完整HTML片段
   */
  private triggerTagComplete(tagName: string, isSelfClosing: boolean): void {
    // 从栈中找到当前标签
    const tagIndex = isSelfClosing
      ? this.tagStack.length - 1
      : // @ts-ignore
        this.tagStack.findLastIndex(
          (item: any) => item.name === tagName && !item.isSelfClosing
        );

    if (tagIndex === -1) return;
    const tagInfo = this.tagStack[tagIndex];

    // 截取完整的HTML片段
    const completeHtml = this.fullHtmlBuffer.slice(tagInfo.start);

    // 构造结果并触发回调
    const result: CompleteTagHtmlResult = {
      tagName: tagInfo.name,
      isSelfClosing: tagInfo.isSelfClosing,
      completeHtml,
      attributes: tagInfo.attrs,
      level: tagInfo.level,
    };
    this.options.onTagComplete(result);

    // 自闭合标签从栈中移除
    if (isSelfClosing) {
      this.tagStack.splice(tagIndex, 1);
    }
  }

  /**
   * 手动添加HTML字符串（支持分块传入）
   * @param html 要解析的HTML字符串（整段/分块）
   */
  public addHtml(html: string): void {
    if (this.isFinished) {
      throw new Error("解析已结束，无法继续添加HTML");
    }
    // 将HTML字符串写入解析器
    this.parser.write(html);
  }

  /**
   * 结束解析（所有HTML已传入时调用）
   */
  public finish(): void {
    if (this.isFinished) return;
    this.isFinished = true;
    this.parser.end();
    this.options.onParseEnd?.();
  }

  /**
   * 重置解析器（清空状态，可重新解析）
   */
  public reset(): void {
    this.fullHtmlBuffer = "";
    this.tagStack = [];
    this.currentLevel = -1;
    this.isFinished = false;
    this.parser = this.createParser(); // 重建解析器
  }
}
